/**
 * Relative Volatility Index (RVI)
 *
 * Similar to RSI but uses standard deviation instead of price change.
 * Measures the direction of volatility on a 0–100 scale.
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const rviIndicator: IndicatorDefinition = {
  id: "rvi",
  name: "Relative Volatility Index",
  shortName: "RVI",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 200 },
    { key: "stdPeriod", label: "StdDev Period", type: "number", default: 10, min: 2, max: 100 },
    { key: "color", label: "Color", type: "color", default: "#00BCD4" },
  ],
  plots: [
    { key: "rvi", label: "RVI", color: "#00BCD4", style: "line", lineWidth: 2, priceScaleId: "rvi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, stdPeriod } = params;
    const src = getSource(candles, "close");
    const data: { time: number; value: number }[] = [];

    if (src.length < stdPeriod + period) return [{ key: "rvi", data }];

    // Calculate standard deviation array
    const stdDev: number[] = [];
    for (let i = 0; i < src.length; i++) {
      if (i < stdPeriod - 1) { stdDev.push(0); continue; }
      let sum = 0;
      for (let j = i - stdPeriod + 1; j <= i; j++) sum += src[j];
      const mean = sum / stdPeriod;
      let sqSum = 0;
      for (let j = i - stdPeriod + 1; j <= i; j++) sqSum += (src[j] - mean) ** 2;
      stdDev.push(Math.sqrt(sqSum / stdPeriod));
    }

    // Apply RSI-like calculation to stdDev
    const upStd: number[] = [];
    const downStd: number[] = [];
    for (let i = stdPeriod; i < src.length; i++) {
      if (src[i] > src[i - 1]) {
        upStd.push(stdDev[i]);
        downStd.push(0);
      } else {
        upStd.push(0);
        downStd.push(stdDev[i]);
      }
    }

    if (upStd.length < period) return [{ key: "rvi", data }];

    let avgUp = 0, avgDown = 0;
    for (let i = 0; i < period; i++) {
      avgUp += upStd[i];
      avgDown += downStd[i];
    }
    avgUp /= period;
    avgDown /= period;

    const offset = stdPeriod + period;
    const rvi = avgDown === 0 ? 100 : 100 * avgUp / (avgUp + avgDown);
    if (offset - 1 < candles.length) {
      data.push({ time: candles[offset - 1].time, value: rvi });
    }

    for (let i = period; i < upStd.length; i++) {
      avgUp = (avgUp * (period - 1) + upStd[i]) / period;
      avgDown = (avgDown * (period - 1) + downStd[i]) / period;
      const val = (avgUp + avgDown) === 0 ? 50 : 100 * avgUp / (avgUp + avgDown);
      const idx = stdPeriod + i;
      if (idx < candles.length) {
        data.push({ time: candles[idx].time, value: val });
      }
    }

    return [{ key: "rvi", data }];
  },
};
